import { IDBPDatabase } from "idb/build/esm/entry";
import VmsDbSchema from "@/models/VmsDbSchema";
import DataService from "@/services/data-service/data-service";
import CompartmentAssessmentService from "@/services/compartment-assessment.service";
import { isBefore, startOfMonth, subMonths } from "date-fns";
import ISearchRequest from "@/models/SearchRequest";
import { BigIntString } from "@/services/BigIntString";
import ICompartmentAssessment from "@/models/compartment-assessment.interface";
import ShipAreaFindingsService from "@/services/ship-area-finding.service";
import ICompartmentAssessmentReportResult from "@/models/msca/ICompartmentAssessmentReportResult";
import IShipAreaFinding from "@/models/ship-finding";
import {
  ActionIdEnum,
  FunctionToRunEnum,
  IMSCADeleteProperties,
  IShipAreaFindingProperties,
  ModulesEnum
} from "@/services/data-service/action-queue-service";
import { ICompartmentAssessmentRequest } from "@/models/compartment-assessment-request";

export default class DataMscaService {
  private compartmentAssessmentService = new CompartmentAssessmentService();
  private shipAreaFindingsService = new ShipAreaFindingsService();
  private readonly CACHE_MONTHS_MSCA_DATA = 10;
  private strInt = new BigIntString();

  constructor(private $db: Promise<IDBPDatabase<VmsDbSchema>>, private $dataService: DataService) {}

  public actions = {
    Add: this.AddCompartmentAssessment.bind(this),
    Delete: this.compartmentAssessmentService.delete,
    Lock: this.LockCompartmentAssessment.bind(this),
    Search: this.Search.bind(this),
    DashBoardExcel: this.compartmentAssessmentService.DashBoardExcel,
    DashBoardPdf: this.compartmentAssessmentService.DashBoardPdf,
    ShipAreasFinding: {
      AddQueue: this.AddShipAreaFindingQueue.bind(this),
      Add: this.AddShipAreaFinding.bind(this),
      UpdateQueue: this.UpdateShipAreaFindingQueue.bind(this),
      Update: this.UpdateShipAreaFinding.bind(this),
      DeleteQueue: this.DeleteShipAreaFindingQueue.bind(this),
      Delete: this.DeleteShipAreaFinding.bind(this),
      SetSatisfactory: this.UpdateSatisfactory.bind(this),
      Sync: this.SyncFinding.bind(this),
      DownloadHighQualityImage: this.DownHighQualityImage.bind(this)
    }
  };

  public getters = {
    GetByShipId: this.GetByShipId.bind(this),
    GetAssessment: this.GetCompartmentAssessment.bind(this),
    GetReport: this.GetReport.bind(this),
    GetShipAreaFindingsReportChart: this.shipAreaFindingsService.getReportChart
  };
  public async initialize(shipIds: number[]) {
    const dateTo = startOfMonth(new Date());
    const dateFrom = subMonths(dateTo, this.CACHE_MONTHS_MSCA_DATA);
    const mscaList = await this.Search({
      dateFrom: dateFrom,
      dateTo: dateTo,
      portsId: []
    });
    return Promise.all(mscaList.map(l => this.GetReport(l.id)));
  }

  public async DownHighQualityImage(assessmentId: string, findingId: string, attachmentId: string) {
    const hqImage = await this.shipAreaFindingsService.getHqImage(attachmentId);
    if (!this.$dataService.getters.isOfflineSupport()) return hqImage;
    const { assessment, findings } = await this.GetCompartmentAssessment(assessmentId);
    const findingIndex = findings.findIndex(x => x.id === findingId);
    if (findingIndex === -1) throw Error("Cannot found the Finding Id");
    if (!findings[findingIndex].attachments) throw Error("There is not any attachment!");
    const attachmentIndex = findings[findingIndex].attachments!.findIndex(
      x => x.id === attachmentId
    );
    if (attachmentIndex === -1) throw Error("Cannot found the Attachment");
    findings[findingIndex].attachments![attachmentIndex].highQuality = hqImage;
    const db = await this.$db;
    const record = await db.get("MscaListData", assessmentId);
    if (!record) throw Error("Cannot found the Compartment Assessment");
    record.findings = DataService.encryptData(findings);
    await db.put("MscaListData", record);
    return hqImage;
  }

  public async AddCompartmentAssessment(args: ICompartmentAssessmentRequest) {
    const dbAssessment = await this.compartmentAssessmentService.add(args);
    return this.$dataService.getters.isOfflineSupport()
      ? this.addAssessmentIndexedDb(dbAssessment)
      : dbAssessment;
  }

  private async addAssessmentIndexedDb(assessment: ICompartmentAssessment) {
    const findings = await this.shipAreaFindingsService.getAll(assessment.id);
    await (
      await this.$db
    ).put("MscaListData", {
      id: assessment.id,
      date: new Date(assessment.issueDate ?? assessment.dateCreated),
      shipId: assessment.shipId,
      assessment: DataService.encryptData(assessment),
      findings: DataService.encryptData(findings),
      maxRowVersion: assessment.rowVersionStr
    });
    return assessment;
  }

  public async LockCompartmentAssessment(id: string, lock: boolean) {
    const dbAssessment = await this.compartmentAssessmentService.lock(id, lock);
    if (!this.$dataService.getters.isOfflineSupport()) return dbAssessment;
    const db = await this.$db;
    const record = await db.get("MscaListData", id);
    if (!record) return this.addAssessmentIndexedDb(dbAssessment);
    record.maxRowVersion = this.strInt.max(record.maxRowVersion, dbAssessment.rowVersionStr);
    record.assessment = DataService.encryptData(dbAssessment);
    await db.put("MscaListData", record);
    return dbAssessment;
  }

  public async AddShipAreaFindingQueue(finding: IShipAreaFinding) {
    if (!this.$dataService.getters.isOfflineSupport() || this.$dataService.getters.isOnline()) {
      return await this.AddShipAreaFinding(finding);
    }
    await this.$dataService.actions.FuncQueue.push({
      functionToRun: FunctionToRunEnum.MSCAAddFinding,
      moduleId: ModulesEnum.MSCA,
      properties: { shipAreaFinding: finding } as IShipAreaFindingProperties,
      msg: "",
      actionId: ActionIdEnum.INSERT
    });
    return finding;
  }

  public async UpdateShipAreaFindingQueue(finding: IShipAreaFinding) {
    if (!this.$dataService.getters.isOfflineSupport() || this.$dataService.getters.isOnline()) {
      return await this.UpdateShipAreaFinding(finding);
    }
    await this.$dataService.actions.FuncQueue.push({
      functionToRun: FunctionToRunEnum.MSCAUpdateFinding,
      moduleId: ModulesEnum.MSCA,
      properties: { shipAreaFinding: finding } as IShipAreaFindingProperties,
      msg: "",
      actionId: ActionIdEnum.UPDATE
    });
    return finding;
  }

  public async DeleteShipAreaFindingQueue(findingId: string, compartmentAssessmentId: string) {
    if (!this.$dataService.getters.isOfflineSupport() || this.$dataService.getters.isOnline()) {
      return await this.DeleteShipAreaFinding(findingId, compartmentAssessmentId);
    }
    await this.$dataService.actions.FuncQueue.push({
      functionToRun: FunctionToRunEnum.MSCADeleteFinding,
      moduleId: ModulesEnum.MSCA,
      properties: { findingId, compartmentAssessmentId } as IMSCADeleteProperties,
      msg: "",
      actionId: ActionIdEnum.UPDATE
    });
    return true;
  }

  public async GetReport(id: string) {
    if (!this.$dataService.getters.isOfflineSupport())
      return (await this.compartmentAssessmentService.getReport(id))?.data;
    return (await this.getReportFromDb(id)) ?? (await this.setReportToDb(id));
  }

  private async getReportFromDb(id: string) {
    const report = await (await this.$db).get("MscaReports", id);
    return report ? DataService.decryptData<ICompartmentAssessmentReportResult>(report) : null;
  }

  private async setReportToDb(id: string) {
    const [report] = await Promise.all([this.compartmentAssessmentService.getReport(id)]);
    if (!report || !report.data) return null;
    await (await this.$db).put("MscaReports", DataService.encryptData(report.data), id);
    return report.data;
  }

  public async SyncFinding(compartmentAssessmentId: string): Promise<boolean> {
    if (!this.$dataService.getters.isOfflineSupport() || this.$dataService.getters.isOffline())
      return false;
    const db = await this.$db;
    const record = await db.get("MscaListData", compartmentAssessmentId);
    if (!record) return false;
    const syncData = await this.shipAreaFindingsService.syncData({
      compartmentAssessmentId,
      rowVersionStr: record.maxRowVersion ?? ""
    });
    if (syncData.findings.length === 0 && syncData.deletedFindings.length === 0) return false;

    const recordFindings = DataService.decryptData<IShipAreaFinding[]>(record.findings);
    let maxRowVersion = record.maxRowVersion;
    for (const f of syncData.findings) {
      const index = recordFindings.findIndex(x => x.id === f.id);
      if (index === -1) recordFindings.push(f);
      else recordFindings[index] = f;
      if (f.rowVersionStr) maxRowVersion = this.strInt.max(maxRowVersion, f.rowVersionStr);
    }
    for (const d of syncData.deletedFindings) {
      const index = recordFindings.findIndex(x => x.id === d.findingId);
      if (index === -1) continue;
      recordFindings.splice(index, 1);
      if (d.rowVersionStr) maxRowVersion = this.strInt.max(maxRowVersion, d.rowVersionStr);
    }
    record.findings = DataService.encryptData(recordFindings);
    record.maxRowVersion = maxRowVersion;
    await (await this.$db).put("MscaListData", record);
    return false;
  }

  public async GetCompartmentAssessment(
    id: string
  ): Promise<{ assessment: ICompartmentAssessment | null; findings: IShipAreaFinding[] }> {
    if (!this.$dataService.getters.isOfflineSupport())
      return {
        assessment: (await this.compartmentAssessmentService.GetOne(id))?.data,
        findings: await this.shipAreaFindingsService.getAll(id)
      };
    const db = await this.$db;
    const data = await db.get("MscaListData", id);
    const findings = data ? DataService.decryptData<IShipAreaFinding[]>(data.findings) : [];
    const assessment = data
      ? DataService.decryptData<ICompartmentAssessment>(data.assessment)
      : null;
    if (data || this.$dataService.getters.isOffline()) return { assessment, findings };

    const [backendAssessment, backendFindings] = await Promise.all([
      (await this.compartmentAssessmentService.GetOne(id))?.data,
      await this.shipAreaFindingsService.getAll(id)
    ]);
    await db.put("MscaListData", {
      id: id,
      date: new Date(backendAssessment.issueDate ?? backendAssessment.dateCreated),
      shipId: backendAssessment.shipId,
      assessment: DataService.encryptData(backendAssessment),
      maxRowVersion: backendAssessment.rowVersionStr,
      findings: DataService.encryptData(backendFindings)
    });
    return { findings: backendFindings, assessment: backendAssessment };
  }

  public async UpdateSatisfactory(
    compartmentAssessmentId: string,
    findingsId: string[],
    value: boolean
  ) {
    const data = await this.shipAreaFindingsService.updateSatisfactory(findingsId, value);
    if (!this.$dataService.getters.isOfflineSupport()) return;
    const db = await this.$db;
    const record = await db.get("MscaListData", compartmentAssessmentId);
    if (!record) throw Error("Cannot found the Compartment Assessment");
    const findings = DataService.decryptData<IShipAreaFinding[]>(record.findings);
    for (const f of findingsId) {
      const index = findings.findIndex(x => x.id === f);
      findings[index].isSatisfactory = true;
    }
    record.findings = DataService.encryptData(findings);
    await db.put("MscaListData", record);
  }

  public async UpdateShipAreaFinding(finding: IShipAreaFinding): Promise<IShipAreaFinding> {
    if (!finding.compartmentAssessmentId)
      throw Error("You must provide a Compartment Assessment Id");
    if (!this.$dataService.getters.isOfflineSupport())
      return this.shipAreaFindingsService.update(finding);

    const data = await this.GetCompartmentAssessment(finding.compartmentAssessmentId);
    if (!data || !data.findings) throw Error("Cannot found the Compartment Assessment");
    const existingFindingIndex = data.findings.findIndex(x => x.id === finding.id);
    if (existingFindingIndex == -1) throw Error("Cannot found the Finding Id");
    const existingFinding = data.findings[existingFindingIndex];
    existingFinding.name = finding.name;
    existingFinding.actionPlan = finding.actionPlan;
    existingFinding.workOrder = finding.workOrder;
    existingFinding.attachments = finding.attachments;
    existingFinding.rating = finding.rating;
    existingFinding.po = finding.po;

    const dbFinding = await this.shipAreaFindingsService.update(finding);
    data.findings[existingFindingIndex] = dbFinding;
    const db = await this.$db;
    const record = await db.get("MscaListData", finding.compartmentAssessmentId);
    if (!record) throw Error("Cannot found the Compartment Assessment");
    record.findings = DataService.encryptData(data.findings);
    record.maxRowVersion = this.strInt.max(record.maxRowVersion, dbFinding.rowVersionStr ?? "0");
    await db.put("MscaListData", record);
    return dbFinding;
  }

  public async AddShipAreaFinding(finding: IShipAreaFinding) {
    if (!finding.compartmentAssessmentId)
      throw Error("You must provide a Compartment Assessment Id");
    const dbFinding = await this.shipAreaFindingsService.add(finding);
    if (!this.$dataService.getters.isOfflineSupport()) return dbFinding;
    const data = await this.GetCompartmentAssessment(finding.compartmentAssessmentId);
    if (!data) throw Error("Cannot found the Compartment Assessment");
    if (!data.findings) data.findings = [];
    data.findings.push(dbFinding);
    const db = await this.$db;
    const record = await db.get("MscaListData", finding.compartmentAssessmentId);
    if (!record) throw Error("Cannot find the Compartment Assessment");
    record.findings = DataService.encryptData(data.findings);
    record.maxRowVersion = this.strInt.max(record.maxRowVersion, dbFinding.rowVersionStr ?? "0");
    await db.put("MscaListData", record);
    return dbFinding;
  }

  public async DeleteShipAreaFinding(shipAreaFindingId: string, compartmentAssessmentId: string) {
    await this.shipAreaFindingsService.delete(shipAreaFindingId);
    if (!this.$dataService.getters.isOfflineSupport()) return;

    const data = await this.GetCompartmentAssessment(compartmentAssessmentId);
    if (!data) throw Error("Cannot found the Compartment Assessment");
    if (!data.findings) return;
    const existingFindingIndex = data.findings?.findIndex(x => x.id === shipAreaFindingId);
    if (existingFindingIndex == -1) throw Error("Cannot found this Finding Id");
    data.findings.splice(existingFindingIndex, 1);
    const db = await this.$db;
    const record = await db.get("MscaListData", compartmentAssessmentId);
    if (!record) return;
    record.findings = DataService.encryptData(data.findings);
    await db.put("MscaListData", record);
  }

  public async GetByShipId(shipId: number) {
    if (!this.$dataService.getters.isOfflineSupport())
      return (await this.compartmentAssessmentService.GetByShipId(shipId))?.data;
    const db = await this.$db;
    const data = (await db.getAllFromIndex("MscaListData", "shipId", shipId)).map(d =>
      DataService.decryptData<ICompartmentAssessment>(d.assessment)
    );
    if (this.$dataService.getters.isOffline()) return data;
    if (!data || data.length < 3) {
      const backendData = (await this.compartmentAssessmentService.GetByShipId(shipId))?.data;
      for (const b of backendData) {
        const findings = await this.shipAreaFindingsService.getAll(b.id);
        await db.put("MscaListData", {
          id: b.id,
          date: new Date(b.issueDate ?? b.dateCreated),
          shipId: b.shipId,
          assessment: DataService.encryptData(b),
          findings: DataService.encryptData(findings),
          maxRowVersion: b.rowVersionStr
        });
      }
      return backendData;
    }
    return data;
  }

  public async Search(args: ISearchRequest) {
    if (!args.dateFrom || !args.dateTo) throw Error("From or To Dates are not defined");

    if (!this.$dataService.getters.isOfflineSupport())
      return (await this.compartmentAssessmentService.Search(args)).data;
    const maxData = await this.getMinMaxRowVersionDate(args.shipId);

    if (maxData) args.rowVersionData = maxData.maxRowVersion;

    if (!args.shipId) {
      return this.$dataService.getters.isOnline()
        ? this.setMscaListData(args)
        : this.getMscaListData(args);
    }

    if (maxData) {
      return isBefore(
        startOfMonth(new Date(args.dateFrom)),
        startOfMonth(new Date(maxData.minDate))
      ) && this.$dataService.getters.isOnline()
        ? await this.setMscaListData(args)
        : await this.getMscaListData(args);
    }
    return this.setMscaListData(args);
  }

  private async getMscaListData(args: ISearchRequest) {
    if (args.dateFrom == undefined || args.dateTo == undefined)
      throw Error("From or To Dates are not defined");
    const data = args.shipId
      ? await (await this.$db).getAllFromIndex("MscaListData", "shipId", args.shipId)
      : await (await this.$db).getAll("MscaListData");
    return data
      .filter(
        d =>
          isBefore(new Date(d.date), new Date(args.dateTo as Date)) &&
          isBefore(new Date(args.dateFrom as Date), new Date(d.date))
      )
      .map(d => DataService.decryptData<ICompartmentAssessment>(d.assessment));
  }
  private async setMscaListData(args: ISearchRequest) {
    if (args.dateFrom == undefined || args.dateTo == undefined)
      throw Error("Date Ranges not defined!");
    const data = (await this.compartmentAssessmentService.Search(args))?.data;
    try {
      for (const d of data) {
        const findings = await this.shipAreaFindingsService.getAll(d.id);
        await (
          await this.$db
        ).put("MscaListData", {
          id: d.id,
          date: new Date(d.issueDate ?? d.dateCreated),
          maxRowVersion: d.rowVersionStr,
          shipId: d.shipId,
          assessment: DataService.encryptData(d),
          findings: DataService.encryptData(findings)
        });
      }
    } catch (e: any) {
      console.error(e);
    }
    return this.getMscaListData(args);
  }

  private async getMinMaxRowVersionDate(shipId?: number) {
    let minDate = startOfMonth(new Date());
    let maxDate = startOfMonth(new Date());
    let maxRowVersion = "0";
    let sendNull = true;
    let cursor = await (await this.$db)
      .transaction("MscaListData")
      .store.index("shipId")
      .openCursor();
    while (cursor) {
      if (!shipId) {
        sendNull = false;
        if (this.strInt.gt(cursor.value.maxRowVersion, maxRowVersion))
          maxRowVersion = cursor.value.maxRowVersion;
      } else if (shipId && cursor.key === shipId) {
        sendNull = false;
        const dbDate = new Date(cursor.value.date);
        if (isBefore(dbDate, minDate)) minDate = dbDate;
        if (isBefore(maxDate, dbDate)) maxDate = dbDate;
        if (this.strInt.gt(cursor.value.maxRowVersion, maxRowVersion))
          maxRowVersion = cursor.value.maxRowVersion;
      }
      cursor = await cursor.continue();
    }
    return sendNull ? null : { minDate, maxDate, maxRowVersion };
  }
}
